import React from 'react';
import './PlayerHeatmap.css';

// Renders a vertical-pitch zone map from the AI's own honest zone estimates
// (leftFlank/center/rightFlank across the pitch width, ownHalf/finalThird
// along its length) — NOT real positional/GPS tracking data, since none
// exists for this footage. The 3x3 grid blends the two axes assuming
// independence, which is a visual approximation, not a precise per-cell
// reading — the caption says so explicitly rather than implying more
// precision than the underlying estimate actually has.
export default function PlayerHeatmap({ zones, color = '#00d4ff' }) {
  if (!zones) {
    return <div className="player-heatmap player-heatmap--empty">Positional data not available for this player</div>;
  }

  const left = zones.leftFlank ?? 33;
  const center = zones.center ?? 34;
  const right = zones.rightFlank ?? 33;
  const own = zones.ownHalf ?? 33;
  const final = zones.finalThird ?? 33;
  const mid = Math.max(0, 100 - own - final);

  const hSum = (left + center + right) || 1;
  const vSum = (own + mid + final) || 1;
  const hW = [left / hSum, center / hSum, right / hSum];
  const vW = [final / vSum, mid / vSum, own / vSum]; // top row = final third (attacking end)

  const cells = [];
  let max = 0;
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      const v = vW[r] * hW[c];
      cells.push({ r, c, v });
      if (v > max) max = v;
    }
  }

  const colW = 80 / 3;
  const rowH = 120 / 3;

  return (
    <div className="player-heatmap">
      <svg viewBox="0 0 80 120" className="player-heatmap__pitch">
        <rect x="1" y="1" width="78" height="118" rx="3" fill="none" stroke="var(--border)" strokeWidth="1" />
        <line x1="1" y1="60" x2="79" y2="60" stroke="var(--border)" strokeWidth="0.6" />
        <circle cx="40" cy="60" r="9" fill="none" stroke="var(--border)" strokeWidth="0.6" />
        {cells.map(({ r, c, v }, i) => {
          const opacity = max > 0 ? 0.1 + 0.8 * (v / max) : 0.1;
          return (
            <rect
              key={i}
              x={c * colW + 1}
              y={r * rowH + 1}
              width={colW - (c === 2 ? 2 : 0)}
              height={rowH - (r === 2 ? 2 : 0)}
              fill={color}
              opacity={opacity}
            />
          );
        })}
      </svg>
      <div className="player-heatmap__caption">Estimated zones of play — AI approximation from footage, not tracked positioning</div>
    </div>
  );
}
